import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { calculateAHP, buildComparisonMatrix, getSaatyLabel, sliderToSaatyValue, saatyValueToSlider } from '../utils/ahp';
import type { PairwiseVote } from '../types';

interface PairwiseComparisonProps {
  items: string[];
  context: string; // e.g., "in the context of [Goal]"
  votes: PairwiseVote[];
  onVoteChange: (votes: PairwiseVote[]) => void;
  onBack: () => void;
  onNext: () => void;
}

export function PairwiseComparison({
  items,
  context,
  votes,
  onVoteChange,
  onBack,
  onNext,
}: PairwiseComparisonProps) {
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({});

  // Generate all pairs
  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      pairs.push([i, j]);
    }
  }

  // Initialize slider values from votes
  useEffect(() => {
    const initial: Record<string, number> = {};
    votes.forEach(vote => {
      const key = `${vote.row}-${vote.col}`;
      initial[key] = saatyValueToSlider(vote.value);
    });
    setSliderValues(initial);
  }, [votes]);

  const currentPair = pairs[currentPairIndex];
  const [row, col] = currentPair;
  const pairKey = `${row}-${col}`;
  const sliderValue = sliderValues[pairKey] ?? 0;

  const handleSliderChange = (value: number) => {
    const newSliderValues = { ...sliderValues, [pairKey]: value };
    setSliderValues(newSliderValues);

    const saatyValue = sliderToSaatyValue(value);
    const newVotes = votes.filter(v => !(v.row === row && v.col === col));
    newVotes.push({ row, col, value: saatyValue });
    onVoteChange(newVotes);
  };

  // Calculate consistency for current state
  const getConsistencyStatus = (): {
    isConsistent: boolean | null;
    message: string;
    result?: ReturnType<typeof calculateAHP>;
  } => {
    if (votes.length < pairs.length) {
      return { isConsistent: null, message: 'Complete all comparisons to check consistency' };
    }

    try {
      const matrix = buildComparisonMatrix(items, votes);
      const result = calculateAHP(matrix);
      
      if (result.is_consistent) {
        return {
          isConsistent: true,
          message: `Consistency Ratio: ${(result.consistency_ratio * 100).toFixed(1)}% (Good)`,
          result,
        };
      } else {
        return {
          isConsistent: false,
          message: `Consistency Ratio: ${(result.consistency_ratio * 100).toFixed(1)}% (Review needed - should be < 10%)`,
          result,
        };
      }
    } catch (error) {
      return { isConsistent: null, message: 'Unable to calculate consistency' };
    }
  };

  const consistencyStatus = getConsistencyStatus();
  const isComplete = votes.length === pairs.length;
  const progress = (votes.length / pairs.length) * 100;

  const getSaatyValueForDisplay = () => {
    if (sliderValue === 0) return { value: 1, label: 'Equal', direction: 'equal' };
    if (sliderValue > 0) {
      const value = Math.round(sliderValue);
      return { value, label: getSaatyLabel(value), direction: 'left' };
    } else {
      const value = Math.round(Math.abs(sliderValue));
      return { value, label: getSaatyLabel(value), direction: 'right' };
    }
  };

  const displayValue = getSaatyValueForDisplay();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Pairwise Comparison</h2>
        <p className="text-[var(--text-sec)]">
          Compare each pair of criteria {context}. Use the slider to indicate your preference.
        </p>
      </div>

      {/* Progress */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-[var(--text-sec)]">
            Comparison {currentPairIndex + 1} of {pairs.length}
          </span>
          <span className="text-sm font-medium text-[var(--text-sec)]">
            {Math.round(progress)}% complete
          </span>
        </div>
        <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
          <div
            className="bg-consulting-slate-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Current Comparison */}
      <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-8">
        <div className="text-center mb-6">
          <p className="text-lg font-medium text-[var(--text-sec)] mb-4">
            Which is more important?
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className={`text-center p-6  border-2 ${
            displayValue.direction === 'left' ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-50' : 'border-[var(--rule)]'
          }`}>
            <h3 className="text-xl font-bold text-[var(--text-pri)] mb-2">{items[row]}</h3>
            {displayValue.direction === 'left' && (
              <span className="text-sm text-consulting-slate-blue-600 font-medium">{displayValue.label}</span>
            )}
          </div>

          <div className={`text-center p-6  border-2 ${
            displayValue.direction === 'right' ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-50' : 'border-[var(--rule)]'
          }`}>
            <h3 className="text-xl font-bold text-[var(--text-pri)] mb-2">{items[col]}</h3>
            {displayValue.direction === 'right' && (
              <span className="text-sm text-consulting-slate-blue-600 font-medium">{displayValue.label}</span>
            )}
          </div>
        </div>

        {/* Slider */}
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm text-[var(--text-sec)] mb-2">
            <span>{items[row]} is much more important</span>
            <span className="font-medium text-[var(--text-pri)]">Equal</span>
            <span>{items[col]} is much more important</span>
          </div>
          
          <input
            type="range"
            min="-9"
            max="9"
            step="1"
            value={sliderValue}
            onChange={(e) => handleSliderChange(Number(e.target.value))}
            className="w-full h-3 bg-[var(--hover-tint)] appearance-none cursor-pointer accent-consulting-slate-blue-600"
          />

          <div className="flex justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-consulting-slate-blue-600">
                {displayValue.direction === 'equal' ? '1' : displayValue.value}
              </div>
              <div className="text-sm text-[var(--text-sec)] mt-1">{displayValue.label}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Consistency Status */}
      {isComplete && consistencyStatus.result && (
        <div className={`p-4  border-2 ${
          consistencyStatus.isConsistent
            ? 'bg-[var(--green-dim)] border-[var(--green)]'
            : 'bg-[var(--coral-dim)] border-[var(--coral)]'
        }`}>
          <div className="flex items-start gap-3">
            {consistencyStatus.isConsistent ? (
              <CheckCircle2 className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${
                consistencyStatus.isConsistent ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'
              }`}>
                {consistencyStatus.message}
              </p>
              {!consistencyStatus.isConsistent && (
                <p className="text-sm text-[var(--coral)] mt-1">
                  Your judgments contain contradictions. Please review your comparisons and adjust them.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-[var(--rule)]">
        <button
          onClick={onBack}
          className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex gap-3">
          {currentPairIndex > 0 && (
            <button
              onClick={() => setCurrentPairIndex(prev => prev - 1)}
              className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
            >
              Previous
            </button>
          )}
          
          {currentPairIndex < pairs.length - 1 ? (
            <button
              onClick={() => setCurrentPairIndex(prev => prev + 1)}
              className="px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700"
            >
              Next Pair
            </button>
          ) : (
            <button
              onClick={onNext}
              disabled={!isComplete || (consistencyStatus.isConsistent === false)}
              className={`px-6 py-2  font-semibold ${
                isComplete && consistencyStatus.isConsistent !== false
                  ? 'bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700'
                  : 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

